import { consts } from '@b2bsoft/pos-platform-common';

import * as wireless from './wireless';
import * as webPos from './webPos';
import { messages } from './consts';

const { posPlatformNames } = consts;
const posPlatformNamesValues = Object.values(posPlatformNames);
const refereshTokenError = {};

/** 
 * Class representing access to POS platform SDKs.
 * Actualy, it is extensible facade, which can call any sdk methods.
 * All common functionality between different POS platforms, 
 * like name, refreshToken, closeApp, has aliases as methods/properties.
 * If you need extneding API, use modules. Details here: {@link /docs/modules-concept.md}
 */
export default class PosPlatform {
    /**
     * @module PosPlatform
     */
    /**
     * The name of SSDK module.
     * @static
     * @memberof PosPlatform
     */
    static salesSdkModuleName = 'ssdk';
    /**
     * The names of all supported POS platforms.
     * @static
     * @memberof PosPlatform
     */
    static names = posPlatformNames;

    static createRefreshTokenError(token) {
        return (refereshTokenError.token = token, refereshTokenError);
    }

    static isRefreshTokenError(error) {
        return refereshTokenError === error;
    }

    // * construction
    /**
     * Create and start initialization of required pos platform by name.
     * Use only supported POS names: (@link PosPlatform.names).
     * Optionally you can provide custom modules, logger, bootstrapSettings, etc. in env paramwter.
     * @class
     * @param {string} name - The name of the POS platform.
     * @param {object} [env] - The optional app environment.
     * @param {object} [env.modules=null] - The optional modules object for them registration.
     * It must have modules names as keys and them factories as values.
     * By default is null.
     * @param {object} [env.withoutInject=false] - The optional flag for delaying POS platform scripts inection.
     * Useful, when you want call inject independently of constuction.
     * By default is false.
     */
    constructor(name, env) {
        this.validatePlatformName(name);

        this.memoizedModules = {};
        this.modules = {};
        this.name = name;
        this.registerModule(
            PosPlatform.salesSdkModuleName,
            () => this.createSsdkModule(false, env),
        );

        const { modules = null, withoutInject = false } = env || {};

        !withoutInject && this.inject(env);
        modules && Object.keys(modules)
            .forEach(key => this.registerModule(key, modules[key]));
    }

    /**
     * Wait for the end of initialization (injection). 
     * Useful when you create POS platform with withoutInject in {@link constructor}
     * and want to wait until injection ends.
     * @async
     * @return {Promise} The Promise which represent initialization result.
     */
    waitInit = async () => {
        if (!this.waitInitPromise) throw new Error(messages.initNotStarted);

        this.initResult = await this.waitInitPromise;

        return this;
    }
    /**
     * Alias for {@link waitInit}
     * @async
     */
    waitInject = async () => this.waitInit();

    // * modules
    /**
     * Register custom module.
     * @param {string} name - The module name.
     * @param {function} factory - The factory for constucting module. Can be async.
     */
    registerModule = (name, factory) => {
        this.modules[name] = factory;
        this.memoizedModules[name] && delete this.memoizedModules[name];

        return this;
    }

    /**
     * Get module by name, if it was previously registered.
     * By default it returns only implementation of you defined POS platform.
     * If you need full functionality of registed module pass true to full parameter.
     * @async
     * @param {string} name - The module name.
     * @param {boolean} [full=false] - The optional flag.
     * By default returned module has only concrecte POS platforms functionallity.
     * If you pass true, then returned full registred and instantiated module.
     * @return {Promise} The Promise, which represents instantiated for POS platform or full module.
     */
    getModule = async (name, full = false) => {
        this.validateModuleName(name);

        let module = this.memoizedModules[name];

        if (!module) {
            this.memoizedModules[name] = module = await this.modules[name](this);
        }

        return full ? module : module[this.name];
    }

    /**
     * Get ssdk module. Support same options, as in getModule.
     * @async
     * @param {...*} - The options of getModule.
     * @return {Promise} The Promise, which represents instantiated for POS platform or full ssdk module.
     */
    getSsdkModule = async (...args) => {
        return await this.getModule(PosPlatform.salesSdkModuleName, ...args);
    }

    // * call sdk
    /**
     * Call or retrieve SSDK method/property of current POS Platfom.
     * Passes arguments to it, if needed
     * @async
     * @param {string} method - The method name
     * @param {...object} args - The optional method arguments
     * @return {Promise} The Promise, which represents called method result or property value
     */
    call = async (method, ...args) => {
        return await this.callPlatform(this.name, method, ...args);
    }

    /**
     * Call or retrieve SSDK method/property of specific POS Platfom
     * Passes arguments to it, if needed
     * @async
     * @param {string} posPlatformName - The platform name
     * @param {string} method - The method name
     * @param {...object} args - The optional method arguments
     * @return {Promise} The Promise, which represents called method result or property value
     */
    callPlatform = async (posPlatformName, methodName, ...args) => {
        return await this.callModule(
            PosPlatform.salesSdkModuleName,
            posPlatformName,
            methodName,
            ...args
        );
    }

    /**
     * Call or retrieve method/property of specific module and specific POS Platfom.
     * Passes arguments to it, if needed.
     * @async
     * @param {string} name - The registered module name
     * @param {string} posPlatformName - The platform name
     * @param {string} methodOrProperty - The method name
     * @param {...object} args - The optional method arguments
     * @return {Promise} The Promise, which represents called method result or property value
     */
    callModule = async (
        name,
        posPlatformName,
        methodOrProperty,
        ...args
    ) => {
        this.validatePlatformName(posPlatformName);

        const fullModule = await this.getModule(name, true);

        if (!fullModule[posPlatformName]) {
            throw new Error(messages.definePlatform);
        }

        const platformModule = fullModule[posPlatformName];

        if (!(methodOrProperty in platformModule)) {
            throw new Error(messages.methodOrPropertyNotExists(name, posPlatformName, methodOrProperty))
        }

        if (typeof platformModule[methodOrProperty] !== 'function') {
            return platformModule[methodOrProperty];
        }

        return await platformModule[methodOrProperty].call(this, ...args);
    }

    // * helpers (only for internal use)
    createWebPosSsdk = (injected, env) => {
        if (!injected) return webPos;

        const instanceName = webPos.createOptions(env).webPosGlobalInstanceName;

        return { ...webPos, ...window[instanceName] };
    }

    createWirelessSsdk = (injected) => {
        if (!injected) return wireless;

        return window && {
            ...wireless,
            ...window.external,
            ...Object.keys(window)
                .filter(key => key.indexOf("WS_") === 0)
                .reduce((obj, key) => (obj[key] = window[key], obj), {}),
        }
    }

    createSsdkModule = (injected = false, env) => {
        return {
            webPos: this.createWebPosSsdk(injected, env),
            wireless: this.createWirelessSsdk(injected, env),
        }
    }

    validateModuleName = (name) => {
        if (!this.modules[name]) {
            throw new Error(messages.moduleNotFound(name));
        }
    }

    validatePlatformName = (name) => {
        if (!name) {
            throw new Error(messages.definePlatform);
        }

        if (!posPlatformNamesValues.includes(name)) {
            throw new Error(messages.unknownPlatform(name));
        }

        return true;
    }

    // * common
    // * Behaviour depeneds on current POS Platfom
    /**
     * Injecting all needed scripts for current POS platfrom.
     * For some platforms injection required some additional settings/service,
     * You can pass them in env
     * @param {*} env The optional app environment needed for injection.
     * @return {Promise} The Promise, which represents injection result
     */
    inject = async (env) => {
        this.waitInitPromise = this.call('inject', env);

        const result = await this.waitInitPromise;
        // second register for rewrite with fully injected ssdk module
        this.registerModule(
            PosPlatform.salesSdkModuleName,
            () => this.createSsdkModule(true, env),
        );

        return result;
    }

    /**
     * Refreshing accessToken
     * @async
     * @return {Promise} The Promise, which represents new accessToken
     */
    refreshToken = async () => {
        try {
            return await this.call('refreshToken');
        } catch (error) {
            throw PosPlatform.createRefreshTokenError(error);
        }
    }

    /**
     * Confirm success
     * @async
     * @return {Promise} The Promise
     */
    confirmSuccess = async (...args) => {
        return await this.call('confirmSuccess', ...args);
    }

    /**
     * Confirm failed
     * @async
     * @return {Promise} The Promise
     */
    confirmFailed = async (...args) => {
        return await this.call('confirmFailed', ...args);
    }

    /**
     * Closes app
     * @async
     * @return {Promise} The Promise
     */
    closeApp = async () => {
        return await this.call('close');
    }

    /**
     * Validate response from ssdk functions and rejects, if response is not valid
     * @async
     * @return {Promise} The Promise
     */
    validateSsdkResponse = async (response) => {
        return await this.call('validateSsdkResponse', response);
    }

    /**
     * Extract data from ssdk response
     * @param {boolean} [validate=false] - The optinal flag for validating respone via {@link validateSsdkResponse}.
     * By default is false.
     * @async
     * @return {Promise} The Promise, which represents extracted payload
     */
    ssdkResponseData = async (response, validate = false) => {
        validate && await this.validateSsdkResponse(response);

        return await this.call('ssdkResponseData', response);
    }
}
